/**
 * Utilities for extracting unique primary ICD codes from submission data.
 *
 * @module getPrimaryDiagnosisCodes
 */
import { useMemo } from "react";
import { Submission } from "../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";

/**
 * Extracts a deduplicated list of primary ICD codes from submission data.
 *
 * @param submissionData - The submission record (may be undefined)
 * @returns Unique ICD code strings; empty array when no data is available
 */
const getPrimaryIcdCodes = (
  submissionData: Submission | undefined,
): string[] => {
  if (!submissionData) return [];

  return Array.from(
    new Set(
      submissionData.primary_diagnosis_details
        ?.filter(
          (item) => item.icd_code !== null && item.icd_code !== undefined,
        )
        .map((item) => item.icd_code as string) ?? [],
    ),
  );
};

/**
 * Hook that memoises the primary ICD codes derived from submission data.
 *
 * @param submissionData - The submission record (may be undefined)
 * @returns Memoised array of unique ICD code strings
 */
export const usePrimaryIcdCodes = (
  submissionData: Submission | undefined,
): string[] => {
  return useMemo(() => getPrimaryIcdCodes(submissionData), [submissionData]);
};
